export type BlogCategoryKey = "seo" | "ads" | "web" | "meo" | "all";

// categories エンドポイント側のフィールドID（ハイフン付き）
const CATEGORY_NAME_FIELD_ID = "category-name";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function pickString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

// blog 側のフィールドID `categories` からカテゴリ名を抽出（参照/複数参照/文字列などを吸収）
export function extractCategoryNameFromCategoriesField(value: unknown): string {
  if (!value) return "";
  if (typeof value === "string") return value;

  if (Array.isArray(value)) {
    const first = value[0] as unknown;
    return first ? extractCategoryNameFromCategoriesField(first) : "";
  }

  if (!isRecord(value)) return "";

  // categories エンドポイントのフィールドIDが `category-name` のため bracket 参照
  const direct = pickString(value[CATEGORY_NAME_FIELD_ID]);
  if (direct) return direct;

  // よくあるフィールド名もフォールバックで拾う
  return (
    pickString(value.name) ||
    pickString(value.title) ||
    pickString(value.category) ||
    pickString(value.label) ||
    pickString(value.value) ||
    ""
  );
}

// microCMS の category.name（例: "SEO対策"）からフィルターキーへ寄せる
export function getBlogCategoryKey(categoryName?: string): BlogCategoryKey {
  if (!categoryName) return "all";
  if (
    categoryName.includes("SEO") ||
    categoryName.toLowerCase().includes("seo")
  ) {
    return "seo";
  }
  if (
    categoryName.includes("広告") ||
    categoryName.includes("Ads") ||
    categoryName.toLowerCase().includes("ads") ||
    categoryName.toUpperCase().includes("AD")
  ) {
    return "ads";
  }
  if (
    categoryName.includes("サイト") ||
    categoryName.includes("Web") ||
    categoryName.toLowerCase().includes("web")
  ) {
    return "web";
  }
  if (
    categoryName.includes("MEO") ||
    categoryName.toLowerCase().includes("meo")
  ) {
    return "meo";
  }
  return "all";
}

// URLに載せるスラッグ（要望: /blog/SEO/{id} のように）
export function blogCategorySlugFromKey(key: BlogCategoryKey): string {
  switch (key) {
    case "seo":
      return "seo";
    case "ads":
      return "ad";
    case "web":
      return "web";
    case "meo":
      return "meo";
    default:
      return "blog";
  }
}

export function blogCategorySlugFromName(categoryName?: string): string {
  return blogCategorySlugFromKey(getBlogCategoryKey(categoryName));
}

// 受け取ったURLセグメントをキーへ正規化（SEO/seo/Seo 等を吸収）
export function blogCategoryKeyFromParam(param?: string): BlogCategoryKey {
  if (!param) return "all";
  const p = decodeURIComponent(param).trim();
  const lower = p.toLowerCase();

  if (lower === "seo" || p.includes("SEO")) return "seo";
  if (
    lower === "ad" ||
    lower === "ads" ||
    p.includes("広告") ||
    p.toUpperCase() === "AD"
  )
    return "ads";
  if (lower === "web" || p.includes("サイト") || p.toUpperCase() === "WEB")
    return "web";
  if (lower === "meo" || p.toUpperCase() === "MEO") return "meo";

  return "all";
}

export function canonicalBlogCategorySlugFromParam(param?: string): string {
  return blogCategorySlugFromKey(blogCategoryKeyFromParam(param));
}
